import { CLAIM_LIST_PATH, ORGANISATION_LIST_PATH, REPOSIT_LIST_PATH, USER_LIST_PATH } from '../../constants/entity-detail-paths';

export type QuickLauncherSearchKind = 'reposits' | 'users' | 'claims' | 'organisations';

interface QuickLauncherDestinationBase {
  id: string;
  label: string;
  prefix: string;
}

export interface QuickLauncherDetailDestination extends QuickLauncherDestinationBase {
  kind: 'detail';
  path: string;
}

export interface QuickLauncherSearchDestination extends QuickLauncherDestinationBase {
  kind: 'search';
  path: string;
  searchKind: QuickLauncherSearchKind;
}

export interface QuickLauncherEvidenceWorkspaceDestination extends QuickLauncherDestinationBase {
  kind: 'evidence-workspace';
}

export type QuickLauncherDestination =
  | QuickLauncherDetailDestination
  | QuickLauncherSearchDestination
  | QuickLauncherEvidenceWorkspaceDestination;

export interface QuickLauncherDestinationOptions {
  canOpenEvidenceWorkspace: boolean;
}

interface QuickLauncherMatcher {
  label: string;
  path: string;
  prefix: string;
}

const ID_MATCHERS: QuickLauncherMatcher[] = [
  {
    label: 'Claim',
    path: CLAIM_LIST_PATH,
    prefix: 'clm_',
  },
  {
    label: 'Reposit',
    path: REPOSIT_LIST_PATH,
    prefix: 'tor_',
  },
  {
    label: 'Organisation',
    path: ORGANISATION_LIST_PATH,
    prefix: 'org_',
  },
  {
    label: 'User',
    path: USER_LIST_PATH,
    prefix: 'usr_',
  },
];

const ID_PATTERN = /^[a-z]{3}_[A-Za-z0-9_-]+$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Key order is the order the searches are offered in.
const EMAIL_SEARCHES: Record<QuickLauncherSearchKind, QuickLauncherMatcher & { preview: string }> = {
  reposits: { label: 'Reposit', path: REPOSIT_LIST_PATH, prefix: 'tor_', preview: 'Search reposits belonging to user' },
  users: { label: 'User', path: USER_LIST_PATH, prefix: 'usr_', preview: 'Search users' },
  claims: { label: 'Claim', path: CLAIM_LIST_PATH, prefix: 'clm_', preview: 'Search claims belonging to user' },
  organisations: { label: 'Organisation', path: ORGANISATION_LIST_PATH, prefix: 'org_', preview: 'Search organisations' },
};

const getIdDestination = (id: string): QuickLauncherDetailDestination | undefined => {
  if (!ID_PATTERN.test(id)) {
    return undefined;
  }

  const matcher = ID_MATCHERS.find((item) => id.startsWith(item.prefix));

  if (!matcher) {
    return undefined;
  }

  return {
    id,
    kind: 'detail',
    label: matcher.label,
    path: `${matcher.path}/${encodeURIComponent(id)}`,
    prefix: matcher.prefix,
  };
};

const getEmailSearchDestinations = (email: string): QuickLauncherSearchDestination[] => {
  if (!EMAIL_PATTERN.test(email)) {
    return [];
  }

  return (Object.keys(EMAIL_SEARCHES) as QuickLauncherSearchKind[]).map((searchKind) => ({
    id: email,
    kind: 'search',
    label: EMAIL_SEARCHES[searchKind].label,
    path: EMAIL_SEARCHES[searchKind].path,
    prefix: EMAIL_SEARCHES[searchKind].prefix,
    searchKind,
  }));
};

const evidenceWorkspaceDestination = (claimId: string): QuickLauncherEvidenceWorkspaceDestination => ({
  id: claimId,
  kind: 'evidence-workspace',
  label: 'Evidence workspace',
  prefix: 'clm_',
});

export const getQuickLauncherDestinations = (
  rawValue: string,
  { canOpenEvidenceWorkspace }: QuickLauncherDestinationOptions,
): QuickLauncherDestination[] => {
  const value = rawValue.trim();
  const idDestination = getIdDestination(value);

  if (!idDestination) {
    return getEmailSearchDestinations(value);
  }

  if (idDestination.prefix === 'clm_' && canOpenEvidenceWorkspace) {
    return [idDestination, evidenceWorkspaceDestination(idDestination.id)];
  }

  return [idDestination];
};

export const getQuickLauncherDestinationKey = (destination: QuickLauncherDestination) =>
  destination.kind === 'search' ? `search:${destination.searchKind}` : `${destination.kind}:${destination.id}`;

export const getQuickLauncherDisplayPrefix = (destination: QuickLauncherDestination) => destination.prefix.replace('_', '');

export const getQuickLauncherPreviewText = (destination: QuickLauncherDestination) => {
  switch (destination.kind) {
    case 'evidence-workspace':
      return 'Open evidence workspace';
    case 'search':
      return EMAIL_SEARCHES[destination.searchKind].preview;
    case 'detail':
      return `Open ${destination.label}`;
  }
};
